import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Brand } from "@/pages/Login";
import { useAuth } from "@/lib/auth";
import { HeroLedger } from "@/components/landing/HeroLedger";
import { HowItWorks } from "@/components/landing/HowItWorks";
import { Industries } from "@/components/landing/Industries";
import { Recorded } from "@/components/landing/Recorded";

const NAV = [["#how", "How it works"], ["#industries", "Who it's for"], ["#privacy", "What we record"], ["/partners", "For MSPs"]];

const Nav = () => {
  const { user } = useAuth();
  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-[#07090E]/85 border-b border-[#222D3D]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="landing-brand-link"><Brand /></Link>
        <nav className="hidden md:flex items-center gap-8 text-sm text-slate-400">
          {NAV.map(([href, label]) => <a key={href} href={href} className="hover:text-slate-100 transition-colors" data-testid={`landing-nav-${href.replace(/\W/g, "")}`}>{label}</a>)}
        </nav>
        {user ? (
          <Button asChild size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold" data-testid="landing-open-dashboard-btn"><Link to="/app">Open dashboard</Link></Button>
        ) : (
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" size="sm" className="text-slate-300" data-testid="landing-signin-btn"><Link to="/login">Sign in</Link></Button>
            <Button asChild size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold" data-testid="landing-get-started-btn"><Link to="/signup">Get started</Link></Button>
          </div>
        )}
      </div>
    </header>
  );
};

const Hero = () => (
  <section className="grid-bg border-b border-[#222D3D]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-[1.05fr_1fr] gap-14 items-center">
      <div className="fade-up">
        <p className="font-mono text-xs text-emerald-400 tracking-[0.3em] uppercase mb-6">AI agent inventory</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.04] tracking-tight text-slate-50" data-testid="landing-hero-title">
          Know every AI agent that crosses your network.
        </h1>
        <p className="mt-6 text-base text-slate-400 leading-relaxed max-w-xl">
          Staff use ChatGPT and Copilot, guests bring their own AI, and outside agents reach your booking and payment systems. Agent Inventory List sits at your building's network gateway, checks every AI agent going in and out, and keeps a tamper-proof list of which ones arrived, where and when.
        </p>
        <p className="mt-5 max-w-xl border-l-2 border-emerald-500 pl-4 text-sm text-slate-300 leading-relaxed" data-testid="landing-hero-visibility-gap">
          <span className="font-semibold text-slate-50">Visibility at the moment of arrival.</span> Most security systems see only some of the devices on your network, which leaves gaps when AI agents show up. The DS-1 hybrid platform records each agent the moment it arrives, so nothing goes unidentified.
        </p>
        <div className="mt-10 flex flex-wrap gap-3">
          <Button asChild size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold" data-testid="landing-hero-cta-signup"><Link to="/signup">Start your inventory</Link></Button>
          <Button asChild size="lg" variant="outline" className="border-[#222D3D] bg-transparent" data-testid="landing-hero-cta-how"><a href="#how">See how it works</a></Button>
        </div>
        <p className="mt-6 text-sm text-slate-400" data-testid="landing-hero-msp">Managed service provider? <Link to="/partners" className="text-emerald-400 hover:text-emerald-300 underline underline-offset-4" data-testid="landing-hero-msp-link">Become a partner and resell under your own brand</Link></p>
        <p className="mt-3 text-xs text-slate-500">Runs on the DS-1 at your gateway. Never records devices, guests or personal data.</p>
      </div>
      <HeroLedger />
    </div>
  </section>
);

const FinalCta = () => (
  <section className="border-t border-[#222D3D] bg-[#0F141C]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 flex flex-col md:flex-row md:items-center justify-between gap-8">
      <div>
        <h2 className="text-2xl sm:text-3xl font-semibold text-slate-50">Know which AI agents are on your premises.</h2>
        <p className="text-slate-400 mt-2 text-sm">Try it with simulated hotel, casino, bank or vendor network traffic in minutes, before any hardware arrives.</p>
      </div>
      <Button asChild size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold" data-testid="landing-final-cta"><Link to="/signup">Create a free account</Link></Button>
    </div>
  </section>
);

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#07090E]" data-testid="landing-page">
      <Nav />
      <Hero />
      <HowItWorks />
      <Industries />
      <Recorded />
      <FinalCta />
      <footer className="border-t border-[#222D3D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row justify-between gap-3 text-xs text-slate-600">
          <span>© {new Date().getFullYear()} AgentInventoryList.com · <Link to="/partners" className="hover:text-slate-300" data-testid="landing-footer-msp-link">MSP partner program</Link></span>
          <span className="font-mono">sha256 · hash chain · bitcoin anchored</span>
        </div>
      </footer>
    </div>
  );
}
